import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Loader2, FileText, Shield,
  CheckCircle2, ChevronRight, RotateCcw, Share2, Copy, Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = { LEAD: "lead", QUESTIONS: "questions", RESULTS: "results" };

function ShareButton({ submissionId }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/score/${submissionId}`;
  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      data-testid="assessment-share-btn"
      onClick={handleCopy}
      className="flex items-center gap-2 border border-slate-700 text-slate-300 px-8 py-3.5 text-xs tracking-[0.15em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
    >
      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4" />}
      {copied ? "Link Copied" : "Share Score"}
    </button>
  );
}

export default function AssessmentPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.LEAD);
  const [lead, setLead] = useState({ name: "", email: "", organization: "" });
  const [leadId, setLeadId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch questions on mount
  useEffect(() => {
    axios.get(`${API}/assessment/questions`).then((res) => {
      setQuestions(res.data.questions);
    });
  }, []);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/assessment/start`, lead);
      setLeadId(res.data.lead_id);
      setStep(STEPS.QUESTIONS);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (questionId, optionIndex) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionIndex }));
  };

  const handleSubmitAssessment = async () => {
    const answerList = Object.entries(answers).map(([qId, idx]) => ({
      question_id: parseInt(qId),
      selected_index: idx,
    }));
    if (answerList.length < questions.length) {
      toast.error("Please answer all questions before submitting.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/assessment/submit`, {
        lead_id: leadId,
        answers: answerList,
      });
      setResults(res.data);
      setStep(STEPS.RESULTS);
    } catch {
      toast.error("Failed to submit assessment.");
    } finally {
      setLoading(false);
    }
  };

  const progress = questions.length > 0
    ? Math.round((Object.keys(answers).length / questions.length) * 100)
    : 0;

  const q = questions[currentQ];

  return (
    <div className="min-h-screen bg-[#050B14]">
      <div className="noise-overlay" aria-hidden="true" />
      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: "#0A1325", border: "1px solid #1E293B", color: "#F8FAFC" },
        }}
      />

      {/* Top Bar */}
      <div className="border-b border-slate-800 bg-[#050B14]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <button
            data-testid="assessment-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-serif text-lg font-bold text-white">JCAM</span>
          </button>
          <span className="text-xs tracking-[0.15em] uppercase text-slate-500">
            Q-Day Readiness Assessment
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <AnimatePresence mode="wait">
          {/* ===== LEAD CAPTURE ===== */}
          {step === STEPS.LEAD && (
            <motion.div
              key="lead"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-12 h-12 border border-[#D4AF37]/30 mb-6">
                  <Shield className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-sans mb-3">
                  Q-Day Readiness Assessment
                </p>
                <h1
                  data-testid="assessment-heading"
                  className="font-serif text-3xl md:text-4xl font-bold text-white tracking-tight mb-4"
                >
                  How Prepared Is Your
                  <br />
                  <span className="text-[#D4AF37]">Institution?</span>
                </h1>
                <p className="text-sm text-slate-400 max-w-lg mx-auto leading-relaxed">
                  10 questions. 3 minutes. Receive your institution's post-quantum
                  readiness score with a detailed breakdown and actionable recommendations.
                </p>
              </div>

              <form onSubmit={handleLeadSubmit} className="space-y-8 max-w-md mx-auto">
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                    Full Name *
                  </label>
                  <input
                    data-testid="assessment-name-input"
                    type="text"
                    value={lead.name}
                    onChange={(e) => setLead({ ...lead, name: e.target.value })}
                    required
                    className="w-full bg-transparent border-b border-slate-700 text-white pb-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                    Email Address *
                  </label>
                  <input
                    data-testid="assessment-email-input"
                    type="email"
                    value={lead.email}
                    onChange={(e) => setLead({ ...lead, email: e.target.value })}
                    required
                    className="w-full bg-transparent border-b border-slate-700 text-white pb-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600"
                    placeholder="you@institution.com"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                    Organization
                  </label>
                  <input
                    data-testid="assessment-org-input"
                    type="text"
                    value={lead.organization}
                    onChange={(e) => setLead({ ...lead, organization: e.target.value })}
                    className="w-full bg-transparent border-b border-slate-700 text-white pb-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600"
                    placeholder="Your institution"
                  />
                </div>
                <button
                  data-testid="assessment-start-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full border border-[#D4AF37] text-[#D4AF37] py-4 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Begin Assessment <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </motion.div>
          )}

          {/* ===== QUESTIONS ===== */}
          {step === STEPS.QUESTIONS && q && (
            <motion.div
              key="questions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {/* Progress bar */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                    Question {currentQ + 1} of {questions.length}
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                    {progress}% Complete
                  </span>
                </div>
                <div className="w-full h-px bg-slate-800 relative">
                  <motion.div
                    className="h-px bg-[#D4AF37] absolute left-0 top-0"
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              {/* Category */}
              <p className="text-xs tracking-[0.15em] uppercase text-[#D4AF37]/60 mb-3">
                {q.category}
              </p>

              {/* Question */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2
                    data-testid={`question-${q.id}`}
                    className="font-serif text-xl md:text-2xl text-white tracking-tight leading-relaxed mb-10"
                  >
                    {q.question}
                  </h2>

                  {/* Options */}
                  <div className="space-y-3">
                    {q.options.map((opt, i) => {
                      const selected = answers[q.id] === i;
                      return (
                        <button
                          key={i}
                          data-testid={`option-${q.id}-${i}`}
                          onClick={() => selectAnswer(q.id, i)}
                          className={`w-full text-left p-5 border transition-all duration-300 flex items-start gap-4 ${
                            selected
                              ? "border-[#D4AF37]/50 bg-[#D4AF37]/5"
                              : "border-slate-800 bg-[#0A1325]/30 hover:border-slate-700"
                          }`}
                        >
                          <span
                            className={`w-5 h-5 border flex-shrink-0 flex items-center justify-center mt-0.5 transition-colors ${
                              selected
                                ? "border-[#D4AF37] bg-[#D4AF37]"
                                : "border-slate-700"
                            }`}
                          >
                            {selected && <CheckCircle2 className="w-3.5 h-3.5 text-[#050B14]" />}
                          </span>
                          <span className={`text-sm leading-relaxed ${selected ? "text-white" : "text-slate-400"}`}>
                            {opt}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-10">
                <button
                  data-testid="assessment-prev-btn"
                  onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
                  disabled={currentQ === 0}
                  className="text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>

                {currentQ < questions.length - 1 ? (
                  <button
                    data-testid="assessment-next-btn"
                    onClick={() => setCurrentQ(currentQ + 1)}
                    disabled={answers[q.id] === undefined}
                    className="border border-slate-700 text-slate-300 px-6 py-2.5 text-xs tracking-[0.15em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    data-testid="assessment-submit-btn"
                    onClick={handleSubmitAssessment}
                    disabled={loading || Object.keys(answers).length < questions.length}
                    className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get Results <ArrowRight className="w-4 h-4" /></>}
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* ===== RESULTS ===== */}
          {step === STEPS.RESULTS && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center mb-12">
                <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-sans mb-6">
                  Your Q-Day Readiness Score
                </p>

                {/* Score circle */}
                <div className="relative w-40 h-40 mx-auto mb-6">
                  <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#1E293B" strokeWidth="3" />
                    <motion.circle
                      cx="60" cy="60" r="52" fill="none"
                      stroke={results.tier_color}
                      strokeWidth="3"
                      strokeDasharray={2 * Math.PI * 52}
                      strokeLinecap="round"
                      initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
                      animate={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - results.score_percentage / 100) }}
                      transition={{ duration: 1.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <motion.span
                      className="font-serif text-4xl font-bold text-white"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8 }}
                    >
                      {results.score_percentage}
                    </motion.span>
                    <span className="text-[10px] tracking-[0.15em] uppercase text-slate-500">/ 100</span>
                  </div>
                </div>

                <h2
                  data-testid="assessment-tier"
                  className="font-serif text-2xl font-bold tracking-tight mb-2"
                  style={{ color: results.tier_color }}
                >
                  {results.tier}
                </h2>
                <p className="text-sm text-slate-400 max-w-lg mx-auto leading-relaxed">
                  {results.summary}
                </p>
              </div>

              {/* Recommendation */}
              <div className="border border-slate-800 bg-[#0A1325]/40 p-8 mb-10">
                <p className="text-xs tracking-[0.15em] uppercase text-[#D4AF37]/60 mb-3">
                  JCAM Recommendation
                </p>
                <p data-testid="assessment-recommendation" className="text-sm text-slate-300 leading-relaxed">
                  {results.recommendation}
                </p>
              </div>

              {/* Breakdown */}
              <div className="mb-10">
                <p className="text-xs tracking-[0.15em] uppercase text-slate-500 mb-6">
                  Category Breakdown
                </p>
                <div className="space-y-4">
                  {results.breakdown.map((item, i) => (
                    <div key={i} data-testid={`breakdown-${i}`} className="border-b border-slate-800/50 pb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-400 tracking-wide">{item.category}</span>
                        <span className="font-serif text-sm font-bold text-white">
                          {item.score}/{item.max_score}
                        </span>
                      </div>
                      <div className="w-full h-1 bg-slate-800 relative">
                        <motion.div
                          className="h-1 absolute left-0 top-0"
                          style={{ backgroundColor: item.score >= 2 ? "#D4AF37" : item.score >= 1 ? "#F59E0B" : "#EF4444" }}
                          initial={{ width: 0 }}
                          animate={{ width: `${(item.score / item.max_score) * 100}%` }}
                          transition={{ duration: 0.8, delay: i * 0.05 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  data-testid="assessment-download-report"
                  href={`${BACKEND_URL}/api/assessment/report/${results.submission_id}`}
                  className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-8 py-3.5 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300"
                >
                  <FileText className="w-4 h-4" />
                  Download Report (PDF)
                </a>
                <ShareButton submissionId={results.submission_id} />
                <button
                  data-testid="assessment-contact-cta"
                  onClick={() => navigate("/")}
                  className="flex items-center gap-2 border border-slate-700 text-slate-300 px-8 py-3.5 text-xs tracking-[0.15em] uppercase hover:border-white hover:text-white transition-all duration-300"
                >
                  Schedule Consultation
                </button>
              </div>

              {/* Retake */}
              <div className="text-center mt-8">
                <button
                  data-testid="assessment-retake-btn"
                  onClick={() => {
                    setStep(STEPS.LEAD);
                    setAnswers({});
                    setCurrentQ(0);
                    setResults(null);
                    setLeadId(null);
                  }}
                  className="text-xs text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1.5 mx-auto"
                >
                  <RotateCcw className="w-3 h-3" /> Retake Assessment
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
